import { FunctionalComponent, h } from 'preact';
import * as appStyles from '../../../assets/global.scss';
import {
  Location,
  Locations,
  LocationSuggestions,
  LocationType,
  TrendingDestinations,
  WidgetTab,
} from '../../../common';
import { actions, Actions, BookingState, connectWithStyles } from '../../../store';
import { WithStyles } from '../../../style';
import { isNotBlank, isNotEmpty, isPresent } from '../../../utils';
import { locationToItem } from '../locationUtils';
import LocateMeButton from './LocateMeButton/LocateMeButton';
import * as styles from './LocationModal.scss';
import LocationModalBase from './LocationModalBase';
import { LocationItem } from './LocationResult/LocationResult';
import { useEffect, useRef } from 'preact/hooks';
import { ModalContext } from '../../../modal';
import { GtmService, WidgetTestVariant } from '../../../config';

export interface LocationModalProps {
  locationType: LocationType;
  isAward?: boolean;
  testVariant: WidgetTestVariant;
}

interface StoreProps {
  locations: Locations;
  results: Location[];
  activeLocationIndex: number;
  suggestions: LocationSuggestions;
  trending: TrendingDestinations;
  loading: boolean;
  geoLocationDisabled: boolean;
  locatingUser: boolean;
  query: string;
  onAmTab: boolean;
  locale: string;
  gtmService: GtmService;
}

type EffectiveProps = LocationModalProps & StoreProps & Actions & WithStyles;

const mapStateToProps = ({
  selection: { locations, locale, activeLocationIndex },
  extensionApi: { gtmService },
  ux: {
    loading: { locationSearch, getSuggestedLocations, getTrendingLocations },
    geoLocationDisabled,
    locatingUser,
  },
  location: { results, suggestions, trending, query },
  currentTab,
}: BookingState): StoreProps => ({
  locations: locations[activeLocationIndex],
  locale,
  gtmService,
  results,
  activeLocationIndex,
  suggestions,
  trending,
  loading: locationSearch || getSuggestedLocations || getTrendingLocations,
  geoLocationDisabled,
  locatingUser,
  query,
  onAmTab: currentTab === WidgetTab.AM,
});

const usePrevious = (value) => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export const LocationModal: FunctionalComponent<LocationModalProps> = connectWithStyles<LocationModalProps>(
  [styles, appStyles],
  {
    mapStateToProps,
    actions,
  }
)(
  (
    {
      locationType,
      results,
      geoLocationDisabled,
      setLocationQuery,
      loading,
      locatingUser,
      suggestions,
      activeLocationIndex,
      trending,
      query,
      onAmTab,
      setAmOrigin,
      setLocations,
      locations,
      locale,
      gtmService,
      isAward,
      setLocatingUser,
    }: EffectiveProps,
    context: ModalContext
  ) => {
    const prevLocatingUser = usePrevious(locatingUser);
    useEffect(() => {
      if (!locatingUser && prevLocatingUser && !geoLocationDisabled) {
        // TODO: Do we need to check if location is found? What happens if it isn't?
        // Display and error somewhere?
        setLocatingUser(false);
        context.modal.hideModal();
      }
    }, [locatingUser]);

    const getLocations = () => {
      let suggestedLocations: Location[];
      let locations: Location[] = [];

      if (locationType === LocationType.ORIGIN) {
        suggestedLocations = suggestions?.departures;
      } else {
        suggestedLocations = isNotEmpty(trending?.locations) ? trending?.locations : suggestions?.destinations;
      }

      if (isPresent(results) && isNotBlank(query)) {
        locations = results;
      } else if (activeLocationIndex === 0 && isPresent(suggestedLocations)) {
        locations = suggestedLocations;
      }

      return isAward ? locations.filter((l) => l.airAward) : locations;
    };

    const listId = `${locationType}-${isPresent(results) ? 'locations' : 'suggestions'}`;
    const locationList = getLocations();
    const locationItems: LocationItem[] = locationList.map(locationToItem);
    const showLocateMe: boolean =
      locationType === LocationType.ORIGIN && !geoLocationDisabled && activeLocationIndex === 0;

    const selectLocation = (selectedLocation: LocationItem): void => {
      const location = locationList.find((loc) => loc.locationCode === selectedLocation.id);
      if (onAmTab) {
        setAmOrigin(location);
      } else {
        const newLocations = { ...locations, [locationType]: location };
        setLocations(newLocations);
      }
    };

    return (
      <LocationModalBase
        locations={locationItems}
        locale={locale}
        onLocationSearch={setLocationQuery}
        onLocationSelect={selectLocation}
        locationType={locationType}
        listId={listId}
        listTopBar={showLocateMe && <LocateMeButton />}
        isLoading={loading}
        disableSearch={locatingUser}
        gtmService={gtmService}
      />
    );
  }
);
